import type { VariantProps } from "class-variance-authority";

import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva } from "class-variance-authority";

import { cn } from "~/pld/lib/cn";

const buttonVariantClasses = cva(
  "flex cursor-pointer items-center justify-center gap-2 rounded text-start select-none focus-visible:shadow-xs focus-visible:ring-4 focus-visible:shadow-gray-100 focus-visible:ring-gray-100 focus-visible:outline-none disabled:pointer-events-none aria-disabled:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        primary:
          "bg-gray-800 text-white hover:bg-gray-600 focus-visible:bg-gray-600 disabled:bg-gray-200 disabled:text-gray-400 aria-disabled:bg-gray-200 aria-disabled:text-gray-400",
        secondary:
          "bg-transparent text-gray-800 inset-ring-[1.5px] inset-ring-gray-300 hover:text-gray-500 hover:inset-ring-gray-300 focus-visible:text-gray-500 focus-visible:inset-ring-gray-300 disabled:text-gray-300 disabled:inset-ring-gray-300 aria-disabled:text-gray-300 aria-disabled:inset-ring-gray-300",
        ghost:
          "bg-transparent text-gray-800 hover:text-gray-500 focus-visible:text-gray-500 disabled:text-gray-300 aria-disabled:text-gray-300",
        link: "bg-transparent text-brand-primary-500 hover:text-brand-primary-400 focus-visible:text-brand-primary-400 focus-visible:shadow-none focus-visible:ring-0 disabled:text-gray-400 aria-disabled:text-gray-400",
        elevated:
          "bg-white text-gray-600 shadow-md hover:bg-gray-25 disabled:bg-white disabled:text-gray-300 aria-disabled:bg-white aria-disabled:text-gray-300",
        "elevated-outline":
          "hover:bg-gray-50 focus-visible:bg-gray-50 disabled:bg-gray-50 aria-disabled:bg-gray-50 bg-white text-gray-800 shadow-xs inset-ring-[1.5px] inset-ring-gray-300 hover:text-gray-800 disabled:text-gray-400 disabled:inset-ring-gray-200 aria-disabled:text-gray-400 aria-disabled:inset-ring-gray-200",
        "secondary-on-dark":
          "bg-transparent text-white shadow-xs inset-ring-[1.5px] inset-ring-gray-300 hover:bg-white/10 hover:text-white focus-visible:bg-white/10 disabled:bg-transparent disabled:text-gray-400 disabled:inset-ring-gray-500 aria-disabled:bg-transparent aria-disabled:text-gray-400 aria-disabled:inset-ring-gray-500",
      },
      size: {
        sm: "px-3.5 py-2 button-sm",
        md: "px-4 py-3 button-md",
        icon: "size-9 p-0",
      },
      destructive: {
        true: "",
        false: "",
      },
      prominent: {
        true: "",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      destructive: false,
      prominent: false,
    },
    compoundVariants: [
      {
        variant: "link",
        size: "sm",
        className: "p-0 button-sm",
      },
      {
        variant: "link",
        size: "md",
        className: "p-0 button-md",
      },
      {
        variant: "primary",
        destructive: true,
        className:
          "bg-error hover:bg-error-dark focus-visible:bg-error-dark focus-visible:inset-ring-1 focus-visible:inset-ring-error disabled:bg-error-light disabled:text-white aria-disabled:bg-error-light aria-disabled:text-white",
      },
      {
        variant: "secondary",
        destructive: true,
        className:
          "text-error inset-ring-error hover:text-error-dark hover:inset-ring-error-dark focus-visible:text-error-dark focus-visible:inset-ring-error-dark disabled:text-error-light disabled:inset-ring-error-light aria-disabled:text-error-light aria-disabled:inset-ring-error-light",
      },
      {
        variant: "ghost",
        destructive: true,
        className:
          "text-error hover:text-error-dark focus-visible:text-error-dark disabled:text-error-light aria-disabled:text-error-light",
      },
      {
        variant: "link",
        destructive: true,
        className:
          "text-error hover:text-error-dark focus-visible:text-error-dark disabled:text-error-light aria-disabled:text-error-light",
      },
      {
        variant: "elevated",
        destructive: true,
        className:
          "text-error hover:text-error-dark focus-visible:text-error-dark disabled:text-error-light aria-disabled:text-error-light",
      },
      {
        variant: "elevated-outline",
        prominent: true,
        className: "inset-ring-gray-800 hover:inset-ring-gray-800",
      },
      {
        variant: "elevated-outline",
        destructive: true,
        className:
          "text-error inset-ring-0 hover:text-error-dark hover:inset-ring-0 disabled:text-error-light aria-disabled:text-error-light",
      },
    ],
  },
);

/**
 * The button classes for a variant, with conflicting utilities already resolved (the link
 * variant's `p-0` over the size padding, for instance), so they can style any element.
 */
function buttonVariants(
  variants?: VariantProps<typeof buttonVariantClasses>,
): string {
  return cn(buttonVariantClasses(variants));
}

export type ButtonProps = Omit<ButtonPrimitive.Props, "className"> &
  VariantProps<typeof buttonVariantClasses> & {
    className?: string;
  };

/**
 * For a link that looks like a button, style the link itself with `buttonVariants`
 * rather than rendering it through `Button`: Base UI gives the rendered element
 * `role="button"`, which would hide the link role.
 */
function Button({
  className,
  variant,
  size,
  destructive,
  prominent,
  onClick,
  ...props
}: ButtonProps) {
  const isAriaDisabled =
    props["aria-disabled"] === true || props["aria-disabled"] === "true";

  return (
    <ButtonPrimitive
      className={cn(
        buttonVariants({ variant, size, destructive, prominent }),
        className,
      )}
      onClick={isAriaDisabled ? undefined : onClick}
      {...props}
    />
  );
}

export { buttonVariants };
export default Button;
